import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  Phone, Search, User, MapPin, Clock, ShoppingCart, 
  ArrowLeft, RefreshCw, Plus, Minus, X, Check,
  History, Star, Save, Euro, Truck, Store
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Category icons
const CATEGORY_ICONS = {
  'Burger': '🍔', 'Pizza': '🍕', 'Wrap': '🌯', 'Salat': '🥗',
  'Pasta': '🍝', 'Suppe': '🍲', 'Fingerfood': '🍗', 'Pizzabrötchen': '🥖',
  'Beilage': '🍟', 'Getränk': '🥤', 'Dessert': '🍰', 'Kids': '👶', 'Sauce': '🥫'
};

export default function PhoneStation({ 
  sessionToken, 
  businessName,
  onBack 
}) {
  // Customer search
  const [searchPhone, setSearchPhone] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // New customer form
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', address: '', notes: '' });
  
  // Products & Cart
  const [products, setProducts] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('delivery'); // delivery or pickup
  
  // Product Options Modal
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [productOptions, setProductOptions] = useState({});
  
  // Loading
  const [loading, setLoading] = useState(true);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/kasse/products`,
        { headers: { 'X-Session-Token': sessionToken } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || {});
        setCategories(data.categories || []);
        setProductOptions(data.options || {});
        if (data.categories?.length > 0) {
          setSelectedCategory(data.categories[0]);
        }
      }
    } catch (error) {
      console.error('Fetch products error:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Search customer
  const searchCustomer = async () => {
    if (!searchPhone && !searchName) {
      toast.error('Bitte Telefon oder Name eingeben');
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/phone/search-customer`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken 
          },
          body: JSON.stringify({ phone: searchPhone, name: searchName })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.customers || []);
        if (data.customers?.length === 0) {
          toast.info('Kein Kunde gefunden - Neu anlegen?');
        }
      }
    } catch (error) {
      toast.error('Suchfehler');
    } finally {
      setSearching(false);
    }
  };

  // Save new customer
  const saveCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone) {
      toast.error('Name und Telefon sind erforderlich');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/phone/save-customer?name=${encodeURIComponent(newCustomer.name)}&phone=${encodeURIComponent(newCustomer.phone)}&address=${encodeURIComponent(newCustomer.address || '')}&notes=${encodeURIComponent(newCustomer.notes || '')}`,
        {
          method: 'POST',
          headers: { 'X-Session-Token': sessionToken }
        }
      );
      
      if (response.ok) {
        toast.success('Kunde gespeichert!');
        setSelectedCustomer({
          name: newCustomer.name,
          phone: newCustomer.phone,
          address: newCustomer.address,
          notes: newCustomer.notes
        });
        setShowNewCustomerModal(false);
        setNewCustomer({ name: '', phone: '', address: '', notes: '' });
      }
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  // Add to cart
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.name === product.name);
      if (existing) {
        return prev.map(item => 
          item.name === product.name 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success(`${product.name} hinzugefügt`);
  };

  // Update cart quantity
  const updateQuantity = (productName, delta) => {
    setCart(prev => prev.map(item => {
      if (item.name === productName) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  };

  // Remove from cart
  const removeFromCart = (productName) => {
    setCart(prev => prev.filter(item => item.name !== productName));
  };

  // Calculate total
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Submit order
  const submitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Warenkorb ist leer');
      return;
    }
    
    if (orderType === 'delivery' && !selectedCustomer?.address) {
      toast.error('Lieferadresse fehlt');
      return;
    }
    
    try {
      const orderData = {
        items: cart.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        total: cartTotal,
        delivery_type: orderType,
        payment_method: 'cash',
        source: 'phone',
        customer: selectedCustomer ? {
          name: selectedCustomer.name,
          phone: selectedCustomer.phone,
          address: selectedCustomer.address
        } : null
      };
      
      const response = await fetch(
        `${API_BASE}/api/cashx/pos/order`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken 
          },
          body: JSON.stringify(orderData)
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`✅ Bestellung #${data.order_number} aufgenommen!`);
        setCart([]);
        setSelectedCustomer(null);
        setSearchResults([]);
        setSearchPhone('');
        setSearchName('');
      } else {
        toast.error('Bestellung fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={onBack}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Phone className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Telefon-Station</h1>
                <p className="text-white/70 text-sm">{businessName}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - 3 Columns */}
      <div className="p-4 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-100px)]">
        
        {/* Column 1: Customer Search & Info */}
        <Card className="bg-gray-800/80 border-gray-700 overflow-hidden">
          <CardHeader className="bg-blue-500/10 pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5" />
              Kunde
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {/* Phone Search */}
            <div>
              <Label className="text-gray-300">Telefonnummer</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  placeholder="0175 123456..."
                  className="bg-gray-700 border-gray-600 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && searchCustomer()}
                />
                <Button onClick={searchCustomer} disabled={searching} className="bg-blue-600">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Name Search */}
            <div>
              <Label className="text-gray-300">oder Name</Label>
              <Input
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Müller, Schmidt..."
                className="bg-gray-700 border-gray-600 text-white mt-1"
                onKeyDown={(e) => e.key === 'Enter' && searchCustomer()}
              />
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && !selectedCustomer && (
              <div className="space-y-2">
                <Label className="text-gray-400">Suchergebnisse:</Label>
                <ScrollArea className="h-32">
                  {searchResults.map((customer, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedCustomer(customer)}
                      className="p-2 bg-gray-700/50 rounded-lg mb-2 cursor-pointer hover:bg-gray-700 transition-colors"
                    >
                      <div className="text-white font-medium">{customer.name}</div>
                      <div className="text-gray-400 text-sm">{customer.phone}</div>
                      {customer.address && (
                        <div className="text-gray-500 text-xs">{customer.address}</div>
                      )}
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
            
            {/* Selected Customer */}
            {selectedCustomer && (
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-green-400 font-medium">Ausgewählter Kunde</span>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setSelectedCustomer(null)}
                      className="text-gray-400 h-6 w-6 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="text-white font-bold">{selectedCustomer.name}</div>
                  <div className="text-gray-300 text-sm">{selectedCustomer.phone}</div>
                  {selectedCustomer.address && (
                    <div className="text-gray-400 text-sm flex items-start gap-1 mt-1">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {selectedCustomer.address}
                    </div>
                  )}
                  {selectedCustomer.last_orders?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-green-500/20">
                      <div className="text-gray-400 text-xs flex items-center gap-1">
                        <History className="w-3 h-3" />
                        Letzte Bestellung: {selectedCustomer.last_orders[0]?.order_number}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* New Customer Button */}
            <Button 
              variant="outline" 
              className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/20"
              onClick={() => {
                setNewCustomer({ name: '', phone: searchPhone, address: '', notes: '' });
                setShowNewCustomerModal(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Neuen Kunden anlegen
            </Button>
            
            {/* Order Type */}
            <div className="pt-4 border-t border-gray-700">
              <Label className="text-gray-300 mb-2 block">Bestellart</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={orderType === 'delivery' ? 'default' : 'outline'}
                  onClick={() => setOrderType('delivery')}
                  className={orderType === 'delivery' 
                    ? 'bg-blue-600' 
                    : 'border-gray-600 text-gray-300'}
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Lieferung
                </Button>
                <Button
                  variant={orderType === 'pickup' ? 'default' : 'outline'}
                  onClick={() => setOrderType('pickup')}
                  className={orderType === 'pickup' 
                    ? 'bg-amber-600' 
                    : 'border-gray-600 text-gray-300'}
                >
                  <Store className="w-4 h-4 mr-2" />
                  Abholung
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Column 2: Products */}
        <Card className="bg-gray-800/80 border-gray-700 overflow-hidden">
          <CardHeader className="bg-amber-500/10 pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Produkte
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {/* Category Tabs */}
            <ScrollArea className="whitespace-nowrap pb-2 mb-2">
              <div className="flex gap-1">
                {categories.map(cat => (
                  <Button
                    key={cat}
                    size="sm"
                    variant={selectedCategory === cat ? 'default' : 'ghost'}
                    onClick={() => setSelectedCategory(cat)}
                    className={selectedCategory === cat 
                      ? 'bg-amber-600 text-white' 
                      : 'text-gray-300 hover:bg-gray-700'}
                  >
                    <span className="mr-1">{CATEGORY_ICONS[cat] || '📦'}</span>
                    {cat}
                  </Button>
                ))}
              </div>
            </ScrollArea>
            
            {/* Product Grid */}
            <ScrollArea className="h-[calc(100vh-320px)]">
              <div className="grid grid-cols-2 gap-2">
                {(products[selectedCategory] || []).map((product, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    onClick={() => addToCart(product)}
                    className="h-auto py-2 px-3 flex flex-col items-start border-gray-600 hover:border-amber-500 hover:bg-amber-500/10"
                  >
                    <span className="text-white text-sm font-medium text-left">
                      {product.name}
                    </span>
                    <span className="text-amber-400 font-bold">
                      {product.price?.toFixed(2)} €
                    </span>
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Column 3: Cart */}
        <Card className="bg-gray-800/80 border-gray-700 overflow-hidden flex flex-col">
          <CardHeader className="bg-green-500/10 pb-3">
            <CardTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Warenkorb
              </span>
              <Badge className="bg-amber-500 text-black">
                {cart.length} Artikel
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 flex-1 flex flex-col">
            {/* Cart Items */}
            <ScrollArea className="flex-1 mb-4">
              {cart.length === 0 ? (
                <div className="text-gray-400 text-center py-8">
                  Warenkorb ist leer
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map((item, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="text-white text-sm">{item.name}</div>
                        <div className="text-amber-400 text-xs">
                          {item.price?.toFixed(2)} €
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateQuantity(item.name, -1)}
                          className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="text-white font-bold w-6 text-center">
                          {item.quantity}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateQuantity(item.name, 1)}
                          className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFromCart(item.name)}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            {/* Total & Submit */}
            <div className="border-t border-gray-700 pt-4 space-y-3">
              <div className="flex items-center justify-between text-xl">
                <span className="text-gray-300">Gesamt:</span>
                <span className="text-green-400 font-bold">
                  {cartTotal.toFixed(2)} €
                </span>
              </div>
              
              <Button
                onClick={submitOrder}
                disabled={cart.length === 0}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold h-12"
              >
                <Check className="w-5 h-5 mr-2" />
                Bestellung aufnehmen
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Customer Modal */}
      <Dialog open={showNewCustomerModal} onOpenChange={setShowNewCustomerModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-400" />
              Neuen Kunden anlegen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Name *</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="Max Mustermann"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Telefon *</Label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="0175 1234567"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Adresse</Label>
              <Textarea
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="Musterstraße 123, 12345 Stadt"
                rows={2}
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Notizen</Label>
              <Textarea
                value={newCustomer.notes}
                onChange={(e) => setNewCustomer({...newCustomer, notes: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="z.B. Allergien, Sonderwünsche..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCustomerModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={saveCustomer} className="bg-blue-600">
              <Save className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
